import {
  useState, useRef, UIEvent,
} from 'react';
import { KeyboardArrowLeft, KeyboardArrowRight, AddCircleRounded } from '@mui/icons-material';
import { Post } from 'lib/types';
import { SinglePostResult } from 'features/Profile/SinglePostResult';
import { FeaturedTag } from 'components/FeaturedTag';
import { POST_CARD_IMG_WIDTH, imagePostUrl } from 'lib/utils/imageUtils';
import { createAltTextForPosts } from 'lib/helpers';

const itemWidth = 215;
interface Props {
  posts: Post[];
  /**
   * If true, the posts in the carousel will not be interactive.
   * This is used to display the carousel in an iframe.
   */
  disableLinks?: boolean;
  justMedia?: boolean;
  onSelectPost?: (postId: number) => void;
}
const toolTipTitle = 'As a Gondola PRO, your posts are randomly featured.';

export const Carousel = ({
  posts, disableLinks = false, justMedia = false, onSelectPost,
}: Props) => {
  const [scrollLeft, setScrollLeft] = useState(0);
  const carousel = useRef<HTMLDivElement>(null);

  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    const scroll = e.currentTarget.scrollLeft;
    setScrollLeft(scroll);
  };

  const goLeft = () => {
    if (!carousel?.current) return;

    if (scrollLeft <= 0) return;
    carousel.current.scrollLeft = scrollLeft - itemWidth;
  };

  const goRight = () => {
    if (!carousel?.current) return;

    carousel.current.scrollLeft = scrollLeft + itemWidth;
  };

  const disableLeft = () => {
    if (!carousel?.current) return true;

    if (carousel.current.scrollLeft <= 0) return true;

    return false;
  };

  const disableRight = () => {
    if (!carousel?.current) return false;

    const sw = carousel.current.scrollWidth;
    const ow = carousel.current.offsetWidth;
    const noScroll = sw === ow;
    const endScroll = sw === ow + carousel.current.scrollLeft;
    if (noScroll || endScroll) return true;

    return false;
  };
  const video = useRef<any>();

  return (
    <div className={`relative overflow-hidden ${justMedia && 'rounded-b-lg'}`}>
      <button
        type="button"
        className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white opacity-70 hover:opacity-100 disabled:opacity-30 rounded-full"
        onClick={goLeft}
        disabled={disableLeft()}
        aria-label="previous"
        hidden={justMedia && posts.length < 2}
      >
        <KeyboardArrowLeft fontSize="large" />
      </button>
      <button
        type="button"
        className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white opacity-70 hover:opacity-100 disabled:opacity-30 rounded-full"
        onClick={goRight}
        disabled={disableRight()}
        aria-label="next"
        hidden={justMedia && posts.length < 2}
      >
        <KeyboardArrowRight fontSize="large" />
      </button>
      <div
        ref={carousel}
        onScroll={onScroll}
        className={`relative flex ${!justMedia && 'gap-4'} overflow-x-scroll scroll-smooth snap-x z-0 items-center`}
      >
        {justMedia ? (
          <>
            {posts.map((post) => (
              <div className="relative snap-start w-full min-w-[300px] rounded-b-xl" key={post.id}>
                {!!onSelectPost && (
                  <div className="absolute top-1 right-1">
                    <button
                      type="button"
                      onClick={() => onSelectPost(post.id)}
                      className="flex items-center justify-between p-2 hover:bg-gray-100 cursor-pointer w-full"
                    >
                      <AddCircleRounded />
                    </button>
                  </div>
                )}
                <img
                  ref={video}
                  alt={createAltTextForPosts(post)}
                  src={imagePostUrl(post.thumbnailUrl, {
                    width: 300,
                    height: 200,
                  })}
                  className="w-full object-cover"
                />
              </div>
            ))}
          </>
        ) : (
          <>
            {posts.map((post) => (
              <div
                key={post.id}
                className="relative snap-start pt-3"
              >
                {!!onSelectPost && (
                  <div className="absolute top-4 right-1 z-50">
                    <button
                      type="button"
                      onClick={() => onSelectPost(post.id)}
                      className="flex p-1 items-center hover:bg-gray-400 rounded-full cursor-pointer w-full"
                    >
                      <AddCircleRounded color="secondary" fontSize="large" />
                    </button>
                  </div>
                )}
                {post.isFeatured && !disableLinks && (
                  <div className="absolute z-50 right-0 top-0">
                    <FeaturedTag
                      title={toolTipTitle}
                      upgradeTitle="Gondola creators with PRO accounts get their posts randomly featured. Sign up for PRO to join them!"
                    />
                  </div>
                )}
                <SinglePostResult
                  post={post}
                  disableLinks={disableLinks}
                  mediaClassName="w-full min-w-[300px] md:min-w-[250px]"
                  fixedSize={{ width: POST_CARD_IMG_WIDTH, height: POST_CARD_IMG_WIDTH }}
                />
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
